import { OCR_LIMITS, startLocalOcr } from './localOcr';

export const ASSESSMENT_LIMITS = Object.freeze({ bytes: OCR_LIMITS.bytes, characters: 20000, criteria: 30 });
const abortError = () => new DOMException('Import annulé.', 'AbortError');
export function validateAssessmentText(text) {
  if (typeof text !== 'string' || text.length > ASSESSMENT_LIMITS.characters) throw Error('Document limité à 20 000 caractères. Réduisez le document ou collez un extrait relu.');
  if (!text.trim()) throw Error('Le document ne contient pas de texte lisible.');
  return text.trim();
}
/** No upload. Reuses same-origin PDF/OCR resources and leaves all extracted text unconfirmed. */
export function startAssessmentImport(file, options = {}) {
  let cancelled = false, child;
  const promise = (async () => {
    if (!file || !file.size || file.size > ASSESSMENT_LIMITS.bytes) throw Error('Choisissez un document non vide de 10 Mo maximum.');
    const ext = file.name?.split('.').pop().toLowerCase();
    let result;
    if (['txt', 'csv'].includes(ext)) {
      options.onProgress?.({ message: 'Lecture locale du document…' });
      result = { text: await file.text(), method: ext, pages: null };
    } else if (['pdf', 'png', 'jpg', 'jpeg', 'webp'].includes(ext)) {
      const type = { pdf: 'application/pdf', png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', webp: 'image/webp' }[ext];
      const input = file.type === type ? file : new Blob([file], { type });
      child = (options.startOcr || startLocalOcr)(input, options);
      result = await child.promise;
    } else throw Error('Format pris en charge : TXT, CSV, PDF, PNG, JPEG ou WebP. Pour Word/DOCX, exportez en PDF ou collez le texte.');
    if (cancelled) throw abortError();
    return { ...result, text: validateAssessmentText(result.text), sourceName: file.name || 'Document' };
  })();
  return { promise, cancel() { cancelled = true; child?.cancel(); } };
}

/** Strict enough to reject malformed input, flexible delimiter and quoted multiline cells. */
export function parseRubricCsv(text, delimiter = ';') {
  validateAssessmentText(text);
  if (![';', ',', '\t'].includes(delimiter)) throw Error('Séparateur CSV invalide.');
  const rows = []; let row = [], cell = '', quoted = false, closed = false;
  const pushCell = () => { row.push(cell.trim()); cell = ''; closed = false; };
  const pushRow = () => { pushCell(); if (row.some(Boolean)) rows.push(row); row = []; };
  const source = text.replace(/^\uFEFF/, '');
  for (let i = 0; i < source.length; i++) {
    const c = source[i];
    if (quoted) {
      if (c === '"') { if (source[i + 1] === '"') { cell += '"'; i++; } else { quoted = false; closed = true; } }
      else cell += c;
    } else if (c === delimiter) pushCell();
    else if (c === '\n' || c === '\r') { if (c === '\r' && source[i + 1] === '\n') i++; pushRow(); }
    else if (c === '"') { if (cell.trim() || closed) throw Error('CSV invalide : guillemet inattendu.'); cell = ''; quoted = true; }
    else if (closed && c.trim()) throw Error('CSV invalide : caractère après un guillemet fermé.');
    else cell += c;
  }
  if (quoted) throw Error('CSV invalide : guillemet non fermé.');
  pushRow();
  if (rows.length < 2) throw Error('Le CSV doit contenir un en-tête et au moins un critère.');
  const headers = rows.shift();
  if (headers.length < 2 || rows.some(r => r.length !== headers.length)) throw Error('Vérifiez le séparateur et le nombre de colonnes du CSV.');
  if (rows.length > ASSESSMENT_LIMITS.criteria) throw Error('La grille est limitée à 30 critères.');
  return { headers, rows };
}
export function mappedRubricCriteria(csv, labelColumn, pointsColumn, id = () => crypto.randomUUID()) {
  const li = Number(labelColumn), pi = Number(pointsColumn);
  if (labelColumn === '' || pointsColumn === '' || !Number.isInteger(li) || !Number.isInteger(pi) || li === pi || li < 0 || pi < 0 || li >= csv.headers.length || pi >= csv.headers.length) throw Error('Choisissez deux colonnes distinctes : intitulé et points maximum.');
  return csv.rows.map(row => ({ id: id(), label: row[li], max: row[pi].replace(',', '.') }));
}
export function confirmAssessment({ name, criteria, totalMax, referenceText = '', answerKey = '', sourceNames = [] }, id = () => crypto.randomUUID()) {
  if (typeof name !== 'string' || !name.trim() || name.trim().length > 200) throw Error('Nommez la grille (200 caractères maximum).');
  if (!Array.isArray(criteria) || !criteria.length || criteria.length > ASSESSMENT_LIMITS.criteria) throw Error('Ajoutez de 1 à 30 critères.');
  const clean = criteria.map(c => {
    const max = Number(String(c.max).replace(',', '.'));
    if (typeof c.label !== 'string' || !c.label.trim() || c.label.trim().length > 250 || !Number.isFinite(max) || max <= 0 || max > 1000) throw Error('Chaque critère exige un intitulé et des points maximum positifs (1 000 au plus).');
    return { id: id(), label: c.label.trim(), max };
  });
  const sum = clean.reduce((n, c) => n + c.max, 0), total = Number(String(totalMax).replace(',', '.'));
  if (!Number.isFinite(total) || total <= 0 || total > 1000 || Math.abs(sum - total) > 0.000001) throw Error('Le total attendu doit correspondre à la somme des critères (1 000 points maximum).');
  for (const value of [referenceText, answerKey]) if (value) validateAssessmentText(value);
  if (!Array.isArray(sourceNames) || sourceNames.length > 2 || sourceNames.some(n => typeof n !== 'string' || n.length > 255)) throw Error('Noms des documents invalides.');
  const rubric = { id: id(), name: name.trim(), criteria: clean, referenceText: referenceText.trim(), answerKey: answerKey.trim(), totalMax: total, sourceNames: sourceNames.slice(0, 2) };
  return { rubric, referenceText: rubric.referenceText, answerKey: rubric.answerKey, totalMax: total, sourceNames: rubric.sourceNames };
}
